/**
 * EVO — sahifa sarlavhasi uchun YAGONA manba.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ MUAMMO: SARLAVHA IKKI JOYDA                                              ║
 * ║                                                                          ║
 * ║ Yuqori panelga sahifa nomi qo'shilgach, ekranlarning o'z `<h1>` i bilan   ║
 * ║ takrorlana boshladi: foydalanuvchi bitta nomni ikki marta ko'radi va      ║
 * ║ ekranning yuqorisidan 40 piksel bekorga ketadi.                          ║
 * ║                                                                          ║
 * ║ «Ekranlardan `<h1>` ni olib tashlash» yechimi YARIM ishlaydi: yuqori      ║
 * ║ panel nomni MENYUDAN oladi, menyuda esa hamma ekran yo'q. Hujjat          ║
 * ║ kartochkalari (`/sales/orders/42`, `/procurement/shipments/7`) va         ║
 * ║ konteyner tannarxi hisoboti menyu bandi EMAS — ular ro'yxatdan            ║
 * ║ ochiladi. Ulardan `<h1>` ni olib tashlasak, sarlavha UMUMAN qolmasdi.     ║
 * ║                                                                          ║
 * ║ YECHIM: sarlavhani ekranning O'ZI e'lon qiladi, yuqori panel esa uni      ║
 * ║ ko'rsatadi. Menyu bandi bo'lgan ekranlar hech narsa qilmaydi — ularga     ║
 * ║ nom menyudan keladi. Hujjat kartochkalari `usePageTitle()` chaqiradi va   ║
 * ║ AYNAN o'sha joyda hujjat raqamini beradi (`SO-2026-00042`).               ║
 * ║                                                                          ║
 * ║ Natija: sarlavha bitta joyda chiziladi, lekin uni kim aytishi ekranga     ║
 * ║ bog'liq. Takrorlanish tuzilmaviy ravishda MUMKIN EMAS.                    ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface PageTitle {
  /** Yirik sarlavha — hujjat raqami yoki ekran nomi. */
  readonly title: string;
  /** Ustidagi kichik qator — bo'lim yoki hujjat turi. */
  readonly kicker?: string;
}

interface PageTitleStore {
  readonly value: PageTitle | null;
  readonly set: (value: PageTitle | null) => void;
}

const PageTitleContext = createContext<PageTitleStore | null>(null);

export function PageTitleProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [value, set] = useState<PageTitle | null>(null);
  const store = useMemo<PageTitleStore>(() => ({ value, set }), [value]);
  return <PageTitleContext.Provider value={store}>{children}</PageTitleContext.Provider>;
}

/** Yuqori panel o'qiydi. Provider bo'lmasa `null` — sarlavha menyudan olinadi. */
export function usePageTitleValue(): PageTitle | null {
  return useContext(PageTitleContext)?.value ?? null;
}

/**
 * Ekran o'z sarlavhasini e'lon qiladi.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ FAQAT MENYUDA BO'LMAGAN EKRANLAR UCHUN                                    │
 * │                                                                           │
 * │ Menyu bandi bo'lgan ekran buni CHAQIRMAYDI: nom allaqachon menyuda bor    │
 * │ va uni ikkinchi joyda takrorlash — bir kun ikkisi ajralib ketishi         │
 * │ demak. Chaqirsa, menyudagi nom bekor bo'ladi (ustunlik shu yerda),        │
 * │ ya'ni xato darhol ko'rinadi.                                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Ekran yopilganda sarlavha O'ZI tozalanadi: aks holda hujjatdan ro'yxatga
 * qaytganda yuqorida eski hujjat raqami qolib ketardi.
 *
 * `title`/`kicker` odatda so'rov natijasidan keladi va ular yuklangunicha
 * `null` bo'ladi — o'shanda yuqori panel menyudagi nomni ko'rsatadi
 * («Sotuv buyurtmalari»), keyin hujjat raqamiga almashadi. Bu «sakrash»
 * emas, aniqlashish: umumiydan xususiyga.
 */
export function usePageTitle(title: string | null, kicker?: string): void {
  const store = useContext(PageTitleContext);

  useEffect(() => {
    if (store === null) return;
    store.set(title === null ? null : { title, kicker });
    return () => store.set(null);
    // `store` har renderda yangi obyekt (`value` o'zgargani uchun), shuning
    // uchun bog'liqlikka `store.set` olinadi — u `useState` dan keladi va
    // BARQAROR. `store` ni qo'ysak, effekt cheksiz aylanardi.
  }, [store?.set, title, kicker]);
}
